import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Mail, Trash2, Plus, X, UserX, UserCheck, ShieldCheck, Plane } from "lucide-react";
import OutOfOfficeDialog, { OooBadge } from "@/components/OutOfOfficeDialog";
import { toast } from "sonner";

const ROLE_ORDER = ["owner", "admin", "chief_estimator", "estimator", "business_development", "project_manager"];
const RANK = { owner: 100, admin: 90, chief_estimator: 50, estimator: 20, business_development: 20, project_manager: 20 };
// Roles the actor may assign/invite: owner is tied to the workspace and never grantable;
// admins may grant only roles below admin.
const assignableRoles = (actorRole) =>
  ROLE_ORDER.filter((r) => r !== "owner" && (actorRole === "owner" || RANK[r] < RANK.admin));

export default function Team() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [oooFor, setOooFor] = useState(null);
  const [invites, setInvites] = useState([]);
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [inv, setInv] = useState({ email: "", role: "estimator", name: "" });
  const [sending, setSending] = useState(false);
  const [wf, setWf] = useState(null);
  const canManage = ["owner", "admin"].includes(user?.role);
  // Whether the current user may change THIS row (role / deactivate / delete). The owner account is
  // untouchable, you can't act on yourself, and only the owner can manage another admin.
  const canManageRow = (u) => {
    if (u.role === "owner" || u.email === user?.email) return false;
    if (user?.role === "owner") return true;
    if (user?.role === "admin") return RANK[u.role] < RANK.admin;
    return false;
  };

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    if (canManage) {
      api.get("/invites").then((r) => setInvites(r.data)).catch(() => {});
      api.get("/auth/domains").then((r) => setDomains(r.data.domains || [])).catch(() => {});
      api.get("/approvals/settings").then((r) => setWf(r.data)).catch(() => {});
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [canManage]);

  const changeRole = async (uid, role) => {
    try {
      await api.put(`/users/${uid}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update role");
    }
  };

  const toggleActive = async (u) => {
    const next = u.is_active === false;
    try {
      await api.put(`/users/${u.user_id}/active`, { active: next });
      toast.success(next ? "User reactivated" : "User deactivated — they've been signed out");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update user");
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name || u.email}? Their sessions and invites are removed and their bids are unassigned. This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.user_id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete user");
    }
  };

  const sendInvite = async () => {
    if (!inv.email.trim()) return;
    setSending(true);
    try {
      await api.post("/invites", inv);
      toast.success(`Invite sent to ${inv.email}`);
      setInv({ email: "", role: "estimator", name: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send invite");
    } finally {
      setSending(false);
    }
  };

  const revokeInvite = async (id) => {
    try {
      await api.delete(`/invites/${id}`);
      load();
    } catch (e) {
      toast.error("Could not revoke invite");
    }
  };

  const saveDomains = async (next) => {
    try {
      const r = await api.put("/auth/domains", { domains: next });
      setDomains(r.data.domains || []);
    } catch (e) {
      toast.error("Could not update domains");
    }
  };
  const addDomain = () => {
    const d = newDomain.trim().toLowerCase().replace(/^@/, "");
    if (!d) return;
    saveDomains([...new Set([...domains, d])]);
    setNewDomain("");
  };

  const changeReportsTo = async (uid, mgr) => {
    try {
      await api.put(`/users/${uid}/reports-to`, { reports_to: mgr });
      toast.success("Reporting line updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update reporting line");
    }
  };

  const saveWf = async (next) => {
    setWf(next);
    try {
      const r = await api.put("/approvals/settings", {
        ...next,
      });
      setWf(r.data);
    } catch (e) {
      toast.error("Could not save workflow settings");
    }
  };

  return (
    <div className="max-w-[1800px] w-full mx-auto px-6 sm:px-8 py-8">
      <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Team &amp; access</h1>
      <p className="text-sm text-slate-500 mt-0.5 mb-6">
        A person's role decides what their plan of the day shows, and what they can do. Access is invite-only.
      </p>

      {canManage && (
        <>
          {/* Invite */}
          <Card className="p-5 mb-6" data-testid="invite-panel">
            <div className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Mail className="w-4 h-4 text-blue-600" /> Invite a teammate
            </div>
            <div className="grid sm:grid-cols-[1fr_1fr_160px_auto] gap-2">
              <Input
                data-testid="invite-email-input"
                placeholder="teammate@company.com"
                value={inv.email}
                onChange={(e) => setInv({ ...inv, email: e.target.value })}
              />
              <Input
                data-testid="invite-name-input"
                placeholder="Name (optional)"
                value={inv.name}
                onChange={(e) => setInv({ ...inv, name: e.target.value })}
              />
              <Select value={inv.role} onValueChange={(v) => setInv({ ...inv, role: v })}>
                <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {assignableRoles(user?.role).map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button
                data-testid="invite-send-button"
                onClick={sendInvite}
                disabled={sending}
                className="bg-[#F4C400] hover:bg-[#e6b000] text-[#000000] font-semibold"
              >
                <Plus className="w-4 h-4 mr-1" /> Invite
              </Button>
            </div>

            {invites.length > 0 && (
              <div className="mt-4 divide-y divide-slate-100 border-t border-slate-100">
                {invites.map((i) => (
                  <div key={i.invite_id} className="flex items-center gap-3 py-2 text-sm" data-testid={`invite-row-${i.invite_id}`}>
                    <span className="font-medium text-slate-800">{i.email}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">{ROLE_LABELS[i.role]}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${i.status === "accepted" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                      {i.status}
                    </span>
                    <button
                      data-testid={`invite-revoke-${i.invite_id}`}
                      onClick={() => revokeInvite(i.invite_id)}
                      className="ml-auto text-slate-400 hover:text-rose-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Approved domains */}
          <Card className="p-5 mb-6" data-testid="domains-panel">
            <div className="text-sm font-semibold text-slate-900 mb-1">Auto-approved email domains</div>
            <p className="text-xs text-slate-500 mb-3">
              Anyone with an email at these domains can sign in without an individual invite.
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              {domains.map((d) => (
                <span key={d} className="inline-flex items-center gap-1.5 text-xs bg-slate-100 text-slate-700 rounded-full px-3 py-1" data-testid={`domain-${d}`}>
                  @{d}
                  <button onClick={() => saveDomains(domains.filter((x) => x !== d))} className="text-slate-400 hover:text-rose-600">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {domains.length === 0 && <span className="text-xs text-slate-400">No domains — invite-only.</span>}
            </div>
            <div className="flex gap-2 max-w-sm">
              <Input
                data-testid="domain-input"
                placeholder="company.com"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addDomain()}
              />
              <Button data-testid="domain-add-button" variant="outline" onClick={addDomain}>Add</Button>
            </div>
          </Card>

          {/* Bid approval workflow */}
          {wf && (
            <Card className="p-5 mb-6" data-testid="workflow-panel">
              <div className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#003E87]" /> Bid approval workflow</div>
              <p className="text-xs text-slate-500 mb-4">Decide who signs off before a bid goes to the client.</p>
              <div className={`mb-3 rounded-md px-3 py-2 text-sm flex items-center gap-2 border ${wf.bdm_signoff ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-slate-50 border-slate-200 text-slate-700"}`} data-testid="wf-mode-live">
                <ShieldCheck className="w-4 h-4 shrink-0" />
                <span>
                  <b>Live mode:</b> {wf.bdm_signoff
                    ? "BDM sign-off is ON — a bid waits for the BDM who owns the lead (or the backup BDM) before it can be sent."
                    : "BDM sign-off is OFF — any estimator with send rights can send a bid. The one rule is BDM sign-off."}
                </span>
              </div>
              <div className="space-y-3">
                <div className="rounded-lg border border-[#003E87]/20 bg-[#003E87]/[0.03] p-3 space-y-3" data-testid="wf-bdm-panel">
                  <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="wf-bdm-signoff">
                    <input type="checkbox" className="mt-0.5 accent-[#003E87]" checked={!!wf.bdm_signoff} onChange={(e) => saveWf({ ...wf, bdm_signoff: e.target.checked })} />
                    <span><b>Require BDM sign-off before a bid is sent.</b>
                      <span className="block text-xs text-slate-500">Goes to the BDM who brought in the lead (Business Development role), otherwise to the backup BDM. Nobody approves their own bid, and a price change after approval needs a new sign-off. Applies to bids created after {wf.signoff_cutoff ? new Date(wf.signoff_cutoff).toLocaleDateString() : "this was switched on"}. Priced-elsewhere bids are never gated.</span>
                    </span>
                  </label>
                  <div className="flex items-center gap-2 text-sm flex-wrap pl-6" data-testid="wf-external-signoff-row">
                    <span className="text-slate-600">Sign-off on priced-elsewhere bids</span>
                    <Select value={wf.external_signoff || "optional"} onValueChange={(v) => saveWf({ ...wf, external_signoff: v })}>
                      <SelectTrigger className="w-40 h-8" data-testid="wf-external-signoff"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="required">Required</SelectItem>
                        <SelectItem value="optional">Optional</SelectItem>
                        <SelectItem value="off">Off</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-slate-500">The assistant or estimator submits an executive brief; approving stays with people. Off = logged after the fact, no sign-off.</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm flex-wrap pl-6">
                    <span className="text-slate-600">Backup BDM</span>
                    <Select value={wf.backup_bdm_id || "none"} onValueChange={(v) => saveWf({ ...wf, backup_bdm_id: v === "none" ? null : v })}>
                      <SelectTrigger className="w-56 h-8" data-testid="wf-backup-bdm"><SelectValue placeholder="Pick a backup BDM" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— No backup BDM —</SelectItem>
                        {users.filter((o) => o.is_active !== false).map((o) => <SelectItem key={o.user_id} value={o.user_id}>{o.name || o.email}{o.role === "business_development" ? " · BDM" : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="wf-escalate">
                  <input type="checkbox" className="mt-0.5 accent-[#003E87]" checked={wf.escalate_to_top !== false} onChange={(e) => saveWf({ ...wf, escalate_to_top: e.target.checked })} />
                  <span><b>Escalate all the way up the chain.</b><span className="block text-xs text-slate-500">Off = only the person's direct manager signs off.</span></span>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="wf-receipts-required">
                  <input type="checkbox" className="mt-0.5 accent-[#003E87]" checked={!!wf.receipts_required} onChange={(e) => saveWf({ ...wf, receipts_required: e.target.checked })} />
                  <span><b>Every number needs a basis before approval.</b><span className="block text-xs text-slate-500">Approval is refused while any line, crew rate, equipment rate, allowance or pay item has no basis (catalog, quote, document, history, or a reason in words). The approver can still acknowledge in words that it goes forward anyway; that acknowledgment is recorded and expires when the estimate changes.</span></span>
                </label>
              </div>
            </Card>
          )}
        </>
      )}

      <Card className="divide-y divide-slate-100">
        {users.map((u) => {
          const initials = (u.name || u.email).split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
          const inactive = u.is_active === false;
          return (
            <div key={u.user_id} className={`flex items-center gap-4 px-4 py-3 ${inactive ? "opacity-60" : ""}`} data-testid={`team-row-${u.user_id}`}>
              <Avatar className="w-9 h-9">
                <AvatarImage src={u.picture} />
                <AvatarFallback className="bg-blue-600 text-white text-xs">{initials}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-2 flex-wrap">
                  {u.name || u.email}
                  {inactive && <span data-testid={`team-inactive-${u.user_id}`} className="text-[10px] font-semibold uppercase tracking-wide text-rose-700 bg-rose-50 border border-rose-100 rounded px-1.5 py-0.5">Deactivated</span>}
                  <OooBadge ooo={u.out_of_office} testid={`team-ooo-${u.user_id}`} />
                  {(canManageRow(u) || u.user_id === user?.user_id) && !inactive && (
                    <button data-testid={`team-ooo-set-${u.user_id}`} onClick={() => setOooFor(u)} title="Out of office" className="text-slate-300 hover:text-[#003E87]"><Plane className="w-3.5 h-3.5" /></button>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate">{u.email}</div>
              </div>
              {canManageRow(u) ? (
                <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v)}>
                  <SelectTrigger className="w-48 [&>span]:truncate" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[...new Set([u.role, ...assignableRoles(user?.role)])].map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-xs px-2.5 py-1 rounded bg-slate-100 text-slate-600">{ROLE_LABELS[u.role]}</span>
              )}
              {canManageRow(u) && (
                <Select value={u.reports_to || "none"} onValueChange={(v) => changeReportsTo(u.user_id, v === "none" ? null : v)}>
                  <SelectTrigger className="w-44 shrink-0 [&>span]:truncate" data-testid={`reports-to-${u.user_id}`}><SelectValue placeholder="Reports to…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No one</SelectItem>
                    {users.filter((o) => o.user_id !== u.user_id).map((o) => <SelectItem key={o.user_id} value={o.user_id}>{o.name || o.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {canManageRow(u) && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="outline" data-testid={`team-toggle-active-${u.user_id}`} onClick={() => toggleActive(u)}
                    className={inactive ? "text-emerald-700 border-emerald-200 hover:bg-emerald-50" : "text-amber-700 border-amber-200 hover:bg-amber-50"}>
                    {inactive ? <><UserCheck className="w-3.5 h-3.5 mr-1" /> Reactivate</> : <><UserX className="w-3.5 h-3.5 mr-1" /> Deactivate</>}
                  </Button>
                  <button data-testid={`team-delete-${u.user_id}`} onClick={() => removeUser(u)} title="Delete user"
                    className="text-slate-300 hover:text-rose-600 px-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>
          );
        })}
      </Card>
    <OutOfOfficeDialog open={!!oooFor} onClose={() => setOooFor(null)} targetId={oooFor?.user_id === user?.user_id ? null : oooFor?.user_id} targetName={oooFor?.name || oooFor?.email} current={oooFor?.out_of_office} onSaved={load} />
    </div>
  );
}
